import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowClockwise, XCircle } from "@phosphor-icons/react";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/ai/jobs").then(r => setJobs(r.data.items));

  useEffect(() => {
    load();
    const iv = setInterval(load, 3500);
    return () => clearInterval(iv);
  }, []);

  const openJob = (j) => api.get(`/ai/jobs/${j.id}`).then(r => setSelected(r.data));

  useEffect(() => {
    if (!selected || selected.status === "done" || selected.status === "cancelled") return;
    const iv = setInterval(() => api.get(`/ai/jobs/${selected.id}`).then(r => setSelected(r.data)), 2500);
    return () => clearInterval(iv);
  }, [selected?.id, selected?.status]);

  const retry = async () => {
    const r = await api.post(`/ai/jobs/${selected.id}/retry`);
    toast.success(`Retrying ${r.data.retried} failed products`);
    api.get(`/ai/jobs/${selected.id}`).then(res => setSelected(res.data));
  };

  const cancel = async () => {
    await api.post(`/ai/jobs/${selected.id}/cancel`);
    toast("Job cancelled");
    load();
  };

  return (
    <div className="space-y-6 min-w-0" data-testid="jobs-page">
      <div>
        <div className="eyebrow mb-1">Background Jobs</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">AI processing history</h1>
        <p className="text-muted-foreground mt-2 text-sm">Bulk AI runs, live progress and retry status.</p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4">
        <div className="card-base overflow-hidden">
          <div className="p-4 border-b border-border section-title">Recent jobs</div>
          <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
            {jobs.length === 0 && <div className="text-sm text-muted-foreground p-6 text-center">No jobs yet. Select products in Catalog → AI Complete.</div>}
            {jobs.map(j => {
              const pct = j.total ? Math.round(((j.completed + j.failed + j.skipped) / j.total) * 100) : 0;
              return (
                <button key={j.id} onClick={() => openJob(j)}
                  className={`w-full text-left p-4 hover:bg-secondary/50 transition-colors ${selected?.id === j.id ? "bg-secondary/60" : ""}`}
                  data-testid={`job-${j.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkle size={14} weight="fill" className="text-orange shrink-0"/>
                      <span className="text-sm font-medium text-navy">AI Complete · {j.total} products</span>
                    </div>
                    <span className={`pill ${j.status === "done" ? "pill-live" : j.status === "cancelled" ? "pill-neutral" : j.status === "running" ? "pill-syncing" : "pill-draft"}`}>{j.status}</span>
                  </div>
                  <div className="text-[11px] font-mono-data text-muted-foreground mb-2">{new Date(j.created_at).toLocaleString()}</div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-brand-green" style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="text-[10px] font-mono-data text-muted-foreground mt-1 tabular-nums">{j.completed}/{j.total} completed · {j.failed} failed</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="card-base p-6 min-w-0">
          {!selected ? (
            <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-lg">Select a job to view details.</div>
          ) : (
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="eyebrow">AI Product Completion</div>
                  <div className="section-title mt-0.5">{selected.completed} / {selected.total} completed</div>
                </div>
                <span className={`pill ${selected.status === "done" ? "pill-live" : selected.status === "cancelled" ? "pill-neutral" : selected.status === "running" ? "pill-syncing" : "pill-draft"}`}>{selected.status}</span>
              </div>

              <div className="h-3 bg-secondary rounded-full overflow-hidden flex mb-4">
                <div className="bg-brand-green" style={{ width: `${(selected.completed / selected.total) * 100}%` }}/>
                <div className="bg-red-500" style={{ width: `${(selected.failed / selected.total) * 100}%` }}/>
                <div className="bg-muted-foreground/40" style={{ width: `${(selected.skipped / selected.total) * 100}%` }}/>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Completed" v={selected.completed} tone="text-brand-green"/>
                <Stat label="Processing" v={selected.processing} tone="text-orange"/>
                <Stat label="Failed" v={selected.failed} tone="text-red-600"/>
                <Stat label="Skipped" v={selected.skipped}/>
              </div>

              <div className="flex gap-2 mb-6">
                {selected.failed > 0 && selected.status === "done" && (
                  <button data-testid="retry-failed" onClick={retry} className="btn-secondary"><ArrowClockwise size={14}/> Retry failed</button>
                )}
                {(selected.status === "running" || selected.status === "queued") && (
                  <button data-testid="cancel-job" onClick={cancel} className="btn-secondary"><XCircle size={14}/> Cancel</button>
                )}
              </div>

              {selected.failures?.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Failed products</div>
                  <div className="border border-border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                    <table className="table-base">
                      <thead><tr><th>Product ID</th><th>Reason</th></tr></thead>
                      <tbody>
                        {selected.failures.map((f, i) => (
                          <tr key={i}>
                            <td className="font-mono-data text-xs truncate max-w-[200px]">{f.product_id.slice(0,8)}…</td>
                            <td className="text-xs text-muted-foreground">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v, tone }) {
  return (
    <div className="border border-border rounded-lg p-3 min-w-0">
      <div className="eyebrow">{label}</div>
      <div className={`font-display text-2xl font-bold tabular-nums ${tone || "text-navy"}`}>{v}</div>
    </div>
  );
}
